import { useState, useRef, useCallback, useId, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import infoCircleIcon from '../../images/infoCircle.svg';
import ExternalLinkModal from '../externalLinkModal/externalLinkModal';
import './infoTooltip.css';

const InfoTooltip = ({ heading, body, tabIndex = 0 }) => {
  const wrapperRef = useRef(null);
  const boxRef = useRef(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  // "You are now leaving..." confirmation modal for external links embedded in
  // the tooltip's `body` HTML (raw <a href="..." target="_blank"> strings,
  // injected via dangerouslySetInnerHTML below). Those are plain DOM anchors
  // with no React click handler of their own, so clicks are intercepted via
  // event delegation (handleBodyClick) rather than per-link React props.
  const [externalLinkModalOpen, setExternalLinkModalOpen] = useState(false);
  const [pendingExternalUrl, setPendingExternalUrl] = useState(null);
  // Stable id for the tooltip content, referenced via aria-describedby below — see
  // hoverTooltip.jsx's matching comment for why the tooltip node must always be
  // rendered (not conditionally mounted only while visible) for screen readers to
  // reliably resolve aria-describedby and actually announce the tooltip's message,
  // rather than just the trigger's "More info about ... button" aria-label.
  const tooltipId = useId();

  // Shared position math, used by both showTooltip (hover/focus) and
  // handleActivate (tap/click toggle) below, and also re-run on every
  // scroll/resize while the tooltip is open (see the effect further down) —
  // previously this only ever ran once at open-time, so on mobile the
  // tooltip stayed pinned at its original screen coordinates as the page
  // scrolled beneath it instead of tracking the icon, the "it followed the
  // scroll [instead of the icon]" complaint.
  const computePosition = useCallback(() => {
    if (!wrapperRef.current) return null;

    const rect = wrapperRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const edgePadding = 16;
    // Extra clearance on the right edge specifically — a feedback/survey overlay
    // widget intermittently docks itself along the right edge of the screen on
    // mobile, and the plain edgePadding wasn't enough to keep the tooltip box
    // from rendering underneath/behind it. Left edge has no such overlay.
    const rightEdgePadding = edgePadding + 10;
    // Estimated box width (its max-width, see infoTooltip.css), used to keep
    // the box within viewport bounds without waiting for a real layout pass.
    // This is only a first-pass guess — the layout effect further down
    // (clampToActualWidth) corrects it against the box's REAL rendered width
    // once available, since the estimate can be wrong (e.g. shorter body text
    // renders a narrower box than this worst-case 400px assumption, under- or
    // over-shifting the box relative to where it actually needs to be).
    const estimatedBoxWidth = 400;
    const anchorX = rect.left + rect.width / 2;
    // By design (per US: arrow should sit a fixed 20px from one end of the box
    // rather than floating to the horizontal center), the box's right edge
    // defaults to 28px right of the trigger's center — 20px arrow inset (see
    // .info-tooltip-arrow's `right: 20px` in infoTooltip.css) + 8px half the
    // arrow triangle's own width — so the arrow points exactly at the trigger
    // with no further adjustment in the common (non-clamped) case.
    const idealRightEdge = anchorX + 28;
    const idealLeftEdge = idealRightEdge - estimatedBoxWidth;

    let shift = 0;
    if (idealLeftEdge < edgePadding) {
      shift = edgePadding - idealLeftEdge;
    } else if (idealRightEdge > viewportWidth - rightEdgePadding) {
      shift = (viewportWidth - rightEdgePadding) - idealRightEdge;
    }

    return {
      top: rect.top - 8,
      left: anchorX,
      // Widen/narrow the arrow's 20px-from-right-edge inset by the same
      // amount the box itself was shifted, so it keeps pointing at the
      // trigger icon.
      arrowOffset: shift,
    };
  }, []);

  const showTooltip = useCallback(() => {
    const pos = computePosition();
    if (pos) setTooltipPos(pos);
  }, [computePosition]);

  const hideTooltip = useCallback(() => {
    setTooltipPos(null);
  }, []);

  const isVisible = !!tooltipPos;

  // Corrects computePosition's arrowOffset guess (based on an ESTIMATED 400px box
  // width) against the box's REAL rendered width, once it's actually in the DOM —
  // mirrors hoverTooltip.jsx's matching clamp effect. Without this, a narrower
  // tooltip (shorter body text) than the worst-case 400px estimate could still
  // render partially off-screen on narrow mobile viewports, since the estimate-based
  // shift computed in computePosition/showTooltip above wasn't necessarily enough
  // (or was too much) for the box's actual size — the "tooltips sometimes drifting
  // off the edge of the screen" report.
  useLayoutEffect(() => {
    if (!isVisible || !boxRef.current || !tooltipPos) return;

    const boxRect = boxRef.current.getBoundingClientRect();
    const leftMargin = 16;
    // Extra clearance on the right edge — see computePosition's matching comment
    // on the feedback/survey overlay that intermittently docks along the right
    // edge of the screen on mobile.
    const rightMargin = leftMargin + 10;
    const viewportWidth = window.innerWidth;

    const unclampedRightEdge = tooltipPos.left + 28;
    const unclampedLeftEdge = unclampedRightEdge - boxRect.width;

    let shift = 0;
    if (unclampedLeftEdge < leftMargin) {
      shift = leftMargin - unclampedLeftEdge;
    } else if (unclampedRightEdge > viewportWidth - rightMargin) {
      shift = (viewportWidth - rightMargin) - unclampedRightEdge;
    }

    // Only update state if the real-width shift meaningfully differs from the
    // estimate already applied, to avoid an infinite re-render loop once the
    // real measurement confirms the estimate was already correct.
    if (Math.abs(shift - (tooltipPos.arrowOffset || 0)) > 0.5) {
      setTooltipPos(prev => (prev ? { ...prev, arrowOffset: shift } : prev));
    }
  }, [isVisible, tooltipPos]);

  // Event delegation for the raw <a> tags injected via dangerouslySetInnerHTML
  // in the tooltip body — intercepts clicks anywhere inside the body, and if
  // the click landed on (or inside) an anchor, prevents the default direct
  // navigation and shows the "you are now leaving" confirmation modal instead.
  const handleBodyClick = useCallback((e) => {
    const anchor = e.target.closest('a[href]');
    if (anchor) {
      e.preventDefault();
      // Stop this click from also bubbling (via React's element-tree-based
      // synthetic event bubbling, not real DOM bubbling — see handleActivate's
      // comment above) up to the trigger wrapper's own onClick/onTouchEnd
      // handler, which would otherwise immediately re-toggle the tooltip
      // right after this closes it.
      e.stopPropagation();
      // Hide the tooltip itself as soon as the modal opens — it's still
      // visually hovered/open at this point (the click happened inside it),
      // so without this its own (higher, see infoTooltip.css) z-index and
      // active pointer-events would sit on top of the modal and swallow
      // clicks meant for its "Go on"/cancel buttons.
      setTooltipPos(null);
      setPendingExternalUrl(anchor.href);
      setExternalLinkModalOpen(true);
    }
  }, []);

  const handleExternalLinkConfirm = useCallback(() => {
    if (pendingExternalUrl) {
      window.open(pendingExternalUrl, '_blank', 'noopener,noreferrer');
    }
    setExternalLinkModalOpen(false);
    setPendingExternalUrl(null);
  }, [pendingExternalUrl]);

  const handleExternalLinkCancel = useCallback(() => {
    setExternalLinkModalOpen(false);
    setPendingExternalUrl(null);
  }, []);

  // Mobile fix: this trigger is frequently rendered inside a <label> for a
  // filter checkbox/radio (see providerFilters.jsx). There's no hover on
  // touch devices, so tapping the icon relied on onFocus alone — but a tap
  // there also fires a native click that bubbles up to the parent <label>,
  // which then redirects focus to (and toggles) the associated input instead
  // of leaving focus on this wrapper, so the tooltip never actually showed.
  // On desktop this was masked because onMouseEnter already displays the
  // tooltip before the click is ever processed. Handling the tap explicitly
  // here — and preventing/stopping it — keeps the activation on this element
  // and stops it from reaching the label at all.
  const handleActivate = useCallback((e) => {
    // Guard against clicks/taps that originated inside the tooltip's own
    // portal content (e.g. the "board-certified"/"accreditation" links
    // rendered via dangerouslySetInnerHTML below). The portal box is mounted
    // to document.body, so it isn't a DOM descendant of this wrapper — but
    // it IS still nested under it in the React element tree, and React
    // bubbles synthetic events (click/touchend) along that tree rather than
    // the real DOM. Without this check, tapping a link on mobile fired
    // handleBodyClick (which opens the "you are now leaving" modal and
    // closes the tooltip) and then ALSO bubbled up to this handler, which
    // immediately toggled the tooltip open/closed again right on top of the
    // modal — surfacing to the user as "the tooltip just closes" instead of
    // the link actually being followed.
    if (boxRef.current?.contains(e.target)) return;
    // Same rationale, but for the "you are now leaving" confirmation modal:
    // it's rendered via its own createPortal call further down, so like the
    // tooltip box it's outside this wrapper in the real DOM but still nested
    // under it in the React element tree, and React bubbles synthetic events
    // along that tree. Without this, tapping "Go on"/cancel inside the modal
    // (on mobile) bubbled up here too and re-toggled the tooltip open again
    // right on top of the modal — reported as the modal "flashing" open with
    // the tooltip stuck on top of it, unable to be interacted with or closed.
    if (externalLinkModalOpen) return;
    e.preventDefault();
    e.stopPropagation();
    setTooltipPos(prev => (prev ? null : computePosition()));
  }, [computePosition, externalLinkModalOpen]);

  // Keep the tooltip "stuck" to its trigger icon while the page scrolls, instead
  // of staying pinned at the fixed screen coordinates captured when it first
  // opened (see computePosition's doc comment above). Also closes it on Escape
  // and on any tap/click outside the trigger or the tooltip box itself — without
  // this, the only way to dismiss a touch-opened tooltip was tapping the (now
  // likely off-screen/scrolled-away) trigger icon again via handleActivate's
  // toggle, which wasn't discoverable and required scrolling back to find it.
  // Mirrors hoverTooltip.jsx's matching effect — see its comments for more detail
  // on why 'pointerdown' (not 'click') and capture:true (for the scroll listener)
  // are used here.
  useEffect(() => {
    if (!isVisible) return undefined;

    const handleScrollOrResize = () => {
      const pos = computePosition();
      if (pos) setTooltipPos(pos);
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        hideTooltip();
        wrapperRef.current?.focus();
      }
    };

    const handlePointerDownOutside = (e) => {
      if (wrapperRef.current?.contains(e.target)) return;
      if (boxRef.current?.contains(e.target)) return;
      hideTooltip();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    document.addEventListener('keydown', handleKeyDown);
    // 'pointerdown' (not 'click') so this also reliably fires on touch devices,
    // and fires before any click-triggered navigation on whatever was tapped.
    document.addEventListener('pointerdown', handlePointerDownOutside);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDownOutside);
    };
  }, [isVisible, computePosition, hideTooltip]);

  return (
    <div
      className="info-tooltip-wrapper"
      ref={wrapperRef}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
      onClick={handleActivate}
      onTouchEnd={handleActivate}
      tabIndex={tabIndex}
      role="button"
      aria-label={`More info about ${heading}`}
      aria-describedby={tooltipId}
    >
      <img src={infoCircleIcon} alt="" className="info-tooltip-icon" />
      {createPortal(
        <div
          id={tooltipId}
          ref={boxRef}
          className="info-tooltip-portal"
          role="tooltip"
          // Also handle enter/leave on the portal content itself (rendered
          // elsewhere in the DOM via the portal, not inside the trigger wrapper
          // above) — without this, moving the mouse off the icon toward a link
          // inside the tooltip body fires the wrapper's onMouseLeave first and
          // hides the tooltip before the click on that link can ever register.
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          style={{
            position: 'fixed',
            top: (tooltipPos?.top ?? 0) + 'px',
            left: (tooltipPos?.left ?? 0) + 'px',
            // Default: box's right edge sits 28px right of the trigger's
            // center (20px arrow inset + 8px half the arrow's own width, see
            // infoTooltip.css), so the fixed 20px-from-right-edge arrow points
            // at the trigger with no further adjustment. `arrowOffset` widens
            // this when the box needed to be clamped on-screen (see showTooltip
            // /handleActivate above).
            transform: `translate(calc(-100% + ${28 + (tooltipPos?.arrowOffset || 0)}px), -100%)`,
            // Visually hide when inactive WITHOUT display:none/visibility:hidden —
            // either would remove the node from the accessibility tree, defeating
            // the point of always rendering it for aria-describedby.
            opacity: isVisible ? 1 : 0,
            pointerEvents: isVisible ? 'auto' : 'none',
          }}
          aria-hidden={!isVisible}
        >
          <div className="info-tooltip-box">
            <div className="info-tooltip-heading">{heading}</div>
            {typeof body === 'string' && /<a\b[^>]*>/i.test(body) ? (
              <div
                className="info-tooltip-body"
                onClick={handleBodyClick}
                dangerouslySetInnerHTML={{ __html: body }}
              />
            ) : (
              <div className="info-tooltip-body">{body}</div>
            )}
          </div>
          {/* Fixed 20px from the box's right edge by default (see
              infoTooltip.css); widened when the box itself has been clamped
              on-screen so it keeps visually pointing at the trigger icon. */}
          <div
            className="info-tooltip-arrow"
            style={{
              right: `calc(20px + ${tooltipPos?.arrowOffset || 0}px)`,
            }}
          ></div>
        </div>,
        document.body
      )}

      {/* Rendered via portal to document.body — same rationale as the tooltip
          portal above (and hoverTooltip.jsx's ExternalLinkModal portal): needs
          to escape any transformed ancestors to render as a proper centered
          fullscreen overlay instead of clipped/mispositioned. */}
      {externalLinkModalOpen && createPortal(
        <ExternalLinkModal
          isOpen={externalLinkModalOpen}
          onConfirm={handleExternalLinkConfirm}
          onCancel={handleExternalLinkCancel}
          url={pendingExternalUrl}
        />,
        document.body
      )}
    </div>
  );
};

export default InfoTooltip;
